/**
 * Phase A Part 2 — Court Order Versions / Supersede.
 *
 * Two pieces:
 *   - <SupersedeOrderDialog /> — captures order_type, variation reason
 *     (+ optional free text for "other"), new effective_from and optional
 *     metadata overrides; POSTs /court-orders/{id}/supersede.
 *   - <CourtOrderLineageTimeline /> — renders the lineage chain as a
 *     readable Interim → Amended → Consent → Final timeline.
 *
 * Both deliberately use calm, neutral language — no "evidence",
 * no judgement words, factual only.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, History, GitBranch, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import {
  ORDER_TYPES,
  ORDER_TYPE_LABEL,
  VARIATION_REASONS,
  VARIATION_REASON_LABEL,
  COUNTRY_JURISDICTIONS,
} from "@/components/courtOrders/courtOrderTypes";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function SupersedeOrderDialog({ open, onOpenChange, order, onSuperseded }) {
  const [form, setForm] = useState({
    effective_from: new Date().toISOString().slice(0, 10),
    order_type: "amended",
    variation_reason: "consent_variation",
    variation_reason_text: "",
    name: "",
    reference: "",
    issuing_court: "",
    judge: "",
    country_jurisdiction: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (order) {
      setForm((f) => ({
        ...f,
        name: order.name || "",
        reference: order.reference || "",
        issuing_court: order.issuing_court || "",
        judge: order.judge || "",
        country_jurisdiction: order.country_jurisdiction || "",
        notes: order.notes || "",
        effective_from: new Date().toISOString().slice(0, 10),
        order_type: "amended",
        variation_reason: "consent_variation",
        variation_reason_text: "",
      }));
    }
  }, [order]);

  if (!order) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.effective_from) {
      toast.error("Pick the date this new version takes effect.");
      return;
    }
    if (
      form.variation_reason === "other" &&
      !(form.variation_reason_text || "").trim()
    ) {
      toast.error("Add a short note explaining the variation reason.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        effective_from: form.effective_from,
        order_type: form.order_type,
        variation_reason: form.variation_reason,
      };
      if (form.variation_reason === "other") {
        payload.variation_reason_text = form.variation_reason_text.trim();
      }
      // Only send overrides that actually differ from the original.
      if (form.name && form.name !== order.name) payload.name = form.name;
      if (form.reference !== order.reference) payload.reference = form.reference;
      if (form.issuing_court !== order.issuing_court)
        payload.issuing_court = form.issuing_court;
      if (form.judge !== order.judge) payload.judge = form.judge;
      if (form.country_jurisdiction !== order.country_jurisdiction)
        payload.country_jurisdiction = form.country_jurisdiction;
      if (form.notes !== order.notes) payload.notes = form.notes;

      const { data } = await api.post(
        `/court-orders/${order.id}/supersede`,
        payload,
      );
      const m = data?.migration || { migrated: 0, flagged: 0, skipped: 0 };
      let msg = "New version created.";
      if (m.migrated > 0 || m.flagged > 0) {
        const parts = [];
        if (m.migrated)
          parts.push(`${m.migrated} record${m.migrated === 1 ? "" : "s"} reassigned`);
        if (m.flagged)
          parts.push(`${m.flagged} flagged for review`);
        msg = `New version created · ${parts.join(" · ")}.`;
      }
      toast.success(msg);
      onOpenChange(false);
      onSuperseded && onSuperseded(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not supersede this order.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3] max-h-[92vh] overflow-y-auto sm:max-w-[640px]"
        data-testid="co-supersede-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            Supersede with a new version
          </DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-[#6F828A] -mt-1">
          A new version of <strong className="text-[#3F535A]">{order.name}</strong>{" "}
          will be created and will take effect from the date below. The current
          version stays intact for historical reference. Unresolved future
          records reassign automatically; records that have been manually
          edited are flagged for your review.
        </p>

        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>New version effective from</Label>
              <Input
                type="date"
                value={form.effective_from}
                onChange={(e) =>
                  setForm({ ...form, effective_from: e.target.value })
                }
                required
                data-testid="co-supersede-effective-from"
              />
            </div>
            <div>
              <Label>Order type</Label>
              <select
                className="w-full h-10 border border-[#D9E1E3] rounded-sm px-3 text-sm bg-white"
                value={form.order_type}
                onChange={(e) => setForm({ ...form, order_type: e.target.value })}
                data-testid="co-supersede-order-type"
              >
                {ORDER_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label>Reason for variation</Label>
            <select
              className="w-full h-10 border border-[#D9E1E3] rounded-sm px-3 text-sm bg-white"
              value={form.variation_reason}
              onChange={(e) =>
                setForm({ ...form, variation_reason: e.target.value })
              }
              data-testid="co-supersede-variation-reason"
            >
              {VARIATION_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          {form.variation_reason === "other" ? (
            <div>
              <Label>Variation reason (short note)</Label>
              <Textarea
                value={form.variation_reason_text}
                onChange={(e) =>
                  setForm({ ...form, variation_reason_text: e.target.value })
                }
                rows={2}
                maxLength={400}
                placeholder="A short note describing the variation."
                data-testid="co-supersede-variation-text"
              />
            </div>
          ) : null}

          <details className="border border-[#D9E1E3] rounded-sm">
            <summary
              className="text-[12px] font-medium text-[#3F535A] px-3 py-2 cursor-pointer select-none"
              data-testid="co-supersede-overrides-toggle"
            >
              Override court details for the new version (optional)
            </summary>
            <div className="px-3 pb-3 space-y-3 pt-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="co-supersede-name"
                  />
                </div>
                <div>
                  <Label>Court reference</Label>
                  <Input
                    value={form.reference}
                    onChange={(e) =>
                      setForm({ ...form, reference: e.target.value })
                    }
                    data-testid="co-supersede-reference"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Country / jurisdiction</Label>
                  <select
                    className="w-full h-10 border border-[#D9E1E3] rounded-sm px-3 text-sm bg-white"
                    value={form.country_jurisdiction}
                    onChange={(e) =>
                      setForm({ ...form, country_jurisdiction: e.target.value })
                    }
                    data-testid="co-supersede-country"
                  >
                    <option value="">—</option>
                    {COUNTRY_JURISDICTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Court</Label>
                  <Input
                    value={form.issuing_court}
                    onChange={(e) =>
                      setForm({ ...form, issuing_court: e.target.value })
                    }
                    data-testid="co-supersede-court"
                  />
                </div>
              </div>
              <div>
                <Label>Judge</Label>
                <Input
                  value={form.judge}
                  onChange={(e) => setForm({ ...form, judge: e.target.value })}
                  data-testid="co-supersede-judge"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  data-testid="co-supersede-notes"
                />
              </div>
            </div>
          </details>

          <div className="border border-[#E6CFA2] bg-[#FBF6EB] rounded-sm p-3 text-[12px] text-[#6F4E1E] flex gap-2 items-start">
            <AlertTriangle className="w-3.5 h-3.5 mt-[2px] flex-shrink-0" />
            <div>
              Sections from the current version will be copied into the new
              one with fresh IDs so editing the new version never alters the
              historical record. You can adjust the new sections after the
              new version is created.
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="co-supersede-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#1F9FA4] hover:bg-[#178085]"
              data-testid="co-supersede-submit"
            >
              {busy ? "Creating…" : "Create new version"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CourtOrderLineageTimeline({ orderId, onJump }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!orderId) return;
    (async () => {
      try {
        const { data: body } = await api.get(`/court-orders/${orderId}/lineage`);
        if (!cancelled) setData(body);
      } catch {
        if (!cancelled) setData({ timeline: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const timeline = data?.timeline || [];
  if (timeline.length <= 1) return null;

  return (
    <div
      className="border border-[#D9E1E3] bg-[#F7FAFA] rounded-md p-3 sm:p-4 space-y-2"
      data-testid="co-lineage-timeline"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="co-lineage-toggle"
      >
        <div className="flex items-center gap-2 text-[#0F5658]">
          <GitBranch className="w-4 h-4" strokeWidth={1.7} />
          <span className="font-heading text-sm font-medium">
            Version history ({timeline.length} versions)
          </span>
        </div>
        <span className="text-[11px] text-[#6F828A]">
          {open ? "Hide" : "Show"}
        </span>
      </button>
      {open ? (
        <ol className="space-y-2 pt-1" data-testid="co-lineage-list">
          {timeline.map((v, idx) => {
            const isCurrent = v.id === orderId;
            const isFinal = !v.superseded_by_id;
            return (
              <li
                key={v.id}
                className={`border rounded-sm bg-white p-3 ${
                  isCurrent
                    ? "border-[#1F9FA4]"
                    : "border-[#D9E1E3]"
                }`}
                data-testid={`co-lineage-entry-${v.id}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] uppercase tracking-[0.12em] text-[#1F9FA4] font-mono-data">
                      v{v.version_index ?? idx + 1}
                    </span>
                    <span className="font-medium text-[#0B2E32] truncate">
                      {v.name || "—"}
                    </span>
                    {v.order_type ? (
                      <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#D9E1E3] rounded-sm bg-[#EEF3F4] text-[#3F535A]">
                        {ORDER_TYPE_LABEL[v.order_type] || v.order_type}
                      </span>
                    ) : null}
                    {isCurrent ? (
                      <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#A7D8DA] bg-[#D6F0EF] text-[#0F5658] rounded-sm">
                        Viewing
                      </span>
                    ) : null}
                    {isFinal && !isCurrent ? (
                      <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#A7D8DA] bg-[#D6F0EF] text-[#0F5658] rounded-sm">
                        Current
                      </span>
                    ) : null}
                  </div>
                  {!isCurrent && onJump ? (
                    <button
                      type="button"
                      onClick={() => onJump(v.id)}
                      className="text-[11px] text-[#1F9FA4] hover:text-[#178085]"
                      data-testid={`co-lineage-jump-${v.id}`}
                    >
                      View this version →
                    </button>
                  ) : null}
                </div>
                <div className="text-[11px] text-[#6F828A] mt-1 font-mono-data">
                  {fmtDate(v.effective_from)} → {fmtDate(v.effective_to)}
                  {v.issuing_court ? ` · ${v.issuing_court}` : ""}
                  {v.judge ? ` · ${v.judge}` : ""}
                </div>
                {v.variation_reason || v.variation_reason_text ? (
                  <div className="text-[11px] text-[#3F535A] mt-1">
                    <span className="text-[#6F828A]">Reason: </span>
                    {VARIATION_REASON_LABEL[v.variation_reason] ||
                      v.variation_reason ||
                      "—"}
                    {v.variation_reason_text ? ` — ${v.variation_reason_text}` : ""}
                  </div>
                ) : null}
                {v.reference ? (
                  <div className="text-[11px] text-[#6F828A] mt-0.5 font-mono-data">
                    Ref: {v.reference}
                  </div>
                ) : null}
                <div className="text-[10px] text-[#6F828A] mt-1">
                  Recorded {fmtTs(v.created_at)}
                  {v.created_by_name ? ` by ${v.created_by_name}` : ""}
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className="flex items-center gap-2 flex-wrap text-[12px] text-[#3F535A]">
          {timeline.map((v, idx) => (
            <React.Fragment key={v.id}>
              <span
                className={`px-2 py-0.5 rounded-sm border ${
                  v.id === orderId
                    ? "border-[#1F9FA4] bg-[#D6F0EF] text-[#0F5658]"
                    : "border-[#D9E1E3] bg-white"
                }`}
              >
                v{v.version_index ?? idx + 1}
                {v.order_type ? ` · ${ORDER_TYPE_LABEL[v.order_type] || v.order_type}` : ""}
              </span>
              {idx < timeline.length - 1 ? (
                <ArrowRight className="w-3 h-3 text-[#6F828A]" />
              ) : null}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

export default SupersedeOrderDialog;
